import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Checkbox, Form, Modal, Radio, Select } from "antd";
import {
  alertSubscriptionRouterCreateAlertSubscriptionV1AlertSubscriptionsPostMutation,
  alertSubscriptionRouterListAlertSubscriptionsV1AlertSubscriptionsGetQueryKey,
  alertSubscriptionRouterUpdateAlertSubscriptionV1AlertSubscriptionsIdPutMutation,
} from "../../services/client/@tanstack/react-query.gen";
import {
  AlertSubscription,
  AlertSubscriptionDto,
  SiteFilterType,
  SiteReadable,
  ValidationEventFilter,
} from "../../services/client/types.gen";
import { useNotify } from "../../components/util-components/Notifications/notifications";
import { useIsViewer } from "../../hooks/useIsViewer";
import { EVENT_FILTER_OPTIONS, SITE_FILTER_LABELS } from "./constants";
import { extractErrorMessage } from "./utils";

type FormValues = {
  emails: string[];
  event_filter: ValidationEventFilter[];
  site_filter_type: SiteFilterType;
  site_ids: string[];
};

const toInitialValues = (subscription: AlertSubscription | null): FormValues =>
  subscription
    ? {
        emails: subscription.channels.map((channel) => channel.email),
        event_filter: subscription.event_filter,
        site_filter_type: subscription.site_filter.type,
        site_ids: subscription.site_filter.site_ids ?? [],
      }
    : {
        emails: [],
        event_filter: [],
        site_filter_type: "ALL_SITES",
        site_ids: [],
      };

type AlertSubscriptionModalProps = {
  open: boolean;
  // The subscription being edited, or null when creating a new one.
  subscription: AlertSubscription | null;
  sites: SiteReadable[];
  onClose: () => void;
};

export const AlertSubscriptionModal: React.FC<AlertSubscriptionModalProps> = ({
  open,
  subscription,
  sites,
  onClose,
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const isViewer = useIsViewer();
  const [form] = Form.useForm<FormValues>();

  const siteFilterType = Form.useWatch("site_filter_type", form);
  const isEditing = !!subscription?._id;

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey:
        alertSubscriptionRouterListAlertSubscriptionsV1AlertSubscriptionsGetQueryKey(),
    });
    notify({
      type: "success",
      message: isEditing
        ? "Alert subscription updated"
        : "Alert subscription created",
    });
    onClose();
  };

  const onError = (error: unknown) => {
    notify({
      type: "error",
      message: isEditing
        ? "Failed to update alert subscription"
        : "Failed to create alert subscription",
      description: extractErrorMessage(error),
    });
  };

  const createMutation = useMutation({
    ...alertSubscriptionRouterCreateAlertSubscriptionV1AlertSubscriptionsPostMutation(),
    onSuccess,
    onError,
  });

  const updateMutation = useMutation({
    ...alertSubscriptionRouterUpdateAlertSubscriptionV1AlertSubscriptionsIdPutMutation(),
    onSuccess,
    onError,
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const onFinish = (values: FormValues) => {
    const body: AlertSubscriptionDto = {
      channels: values.emails.map((email) => ({
        channel: "MAIL",
        email: email.trim(),
      })),
      event_filter: values.event_filter,
      site_filter: {
        type: values.site_filter_type,
        site_ids: values.site_filter_type === "FILTERED" ? values.site_ids : [],
      },
    };

    if (subscription?._id) {
      updateMutation.mutate({ path: { id: subscription._id }, body });
    } else {
      createMutation.mutate({ body });
    }
  };

  const siteOptions = sites.map((site) => ({
    label: site.name ? `${site.name} (${site.site_id})` : site.site_id,
    value: site.site_id,
  }));

  return (
    <Modal
      title={isEditing ? "Edit alert subscription" : "New alert subscription"}
      open={open}
      destroyOnClose
      onCancel={onClose}
      okText={isEditing ? "Save" : "Create"}
      confirmLoading={isPending}
      okButtonProps={{ disabled: isViewer }}
      onOk={() => form.submit()}
    >
      {open && (
        <Form<FormValues>
          key={subscription?._id ?? "new"}
          form={form}
          layout="vertical"
          initialValues={toInitialValues(subscription)}
          onFinish={onFinish}
          disabled={isViewer}
          className="mt-4"
        >
          <Form.Item
            name="emails"
            label="Email addresses"
            tooltip="These addresses will be notified when a matching validation error occurs."
            rules={[
              { required: true, message: "Add at least one email address" },
              {
                type: "array",
                defaultField: {
                  type: "email",
                  message: "Invalid email address",
                },
              },
            ]}
          >
            <Select
              mode="tags"
              open={false}
              tokenSeparators={[",", " ", ";"]}
              placeholder="name@example.com"
            />
          </Form.Item>

          <Form.Item
            name="event_filter"
            label="Alert on"
            rules={[
              {
                required: true,
                message: "Select at least one validation error to alert on",
              },
            ]}
          >
            <Checkbox.Group options={EVENT_FILTER_OPTIONS} />
          </Form.Item>

          <Form.Item name="site_filter_type" label="Applies to">
            <Radio.Group>
              <Radio value="ALL_SITES">{SITE_FILTER_LABELS.ALL_SITES}</Radio>
              <Radio value="FILTERED">{SITE_FILTER_LABELS.FILTERED}</Radio>
            </Radio.Group>
          </Form.Item>

          {siteFilterType === "FILTERED" && (
            <Form.Item
              name="site_ids"
              label="Sites"
              rules={[
                {
                  required: true,
                  message: "Select at least one site",
                },
              ]}
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Select sites"
                options={siteOptions}
              />
            </Form.Item>
          )}

          {(createMutation.isError || updateMutation.isError) && (
            <Alert
              type="error"
              showIcon
              message="Something went wrong"
              description={extractErrorMessage(
                createMutation.error ?? updateMutation.error,
              )}
            />
          )}
        </Form>
      )}
    </Modal>
  );
};
